import React from 'react'
import { Company } from 'models'
import { sortEntities } from 'utils/helpers/sortEntities'
import { ALL_ACCOUNTS_COMPANY, MAX_SELECTED_CHILDREN } from '../../constants'
import { companiesActions, type CompaniesReducerAction } from '../../reducer'
import { reconcileSelection, areSelectionsEqual } from '../../helpers/selection'

const useChildrenList = (params: {
  dispatch: React.Dispatch<CompaniesReducerAction>
  childrenQuery: { data?: Array<Company>; isSuccess: boolean }
  isOrphanSelected: boolean
  isUsingRemoteChildren: boolean
  selectionLimit: number
  pendingSelection: Array<Company>
  selectedChildren: Array<Company>
  isAllAccountsSelected: boolean
  pendingAllAccountsSelection: boolean
  isLoginB2C: boolean
}) => {
  const {
    dispatch,
    childrenQuery,
    isOrphanSelected,
    isUsingRemoteChildren,
    selectionLimit,
    pendingSelection,
    selectedChildren,
    isAllAccountsSelected,
    pendingAllAccountsSelection,
    isLoginB2C,
  } = params

  React.useEffect(() => {
    if (!childrenQuery.isSuccess || !childrenQuery.data || isOrphanSelected) return

    const sortedChildren = sortEntities([...childrenQuery.data])

    const hasChildren = sortedChildren.length > 0
    const shouldIncludeAllAccountsOption = hasChildren && !isUsingRemoteChildren && !isLoginB2C
    const isAllAccountsSelectable =
      shouldIncludeAllAccountsOption && sortedChildren.length <= MAX_SELECTED_CHILDREN
    const childrenList = shouldIncludeAllAccountsOption
      ? [ALL_ACCOUNTS_COMPANY, ...sortedChildren]
      : sortedChildren

    dispatch({ type: companiesActions.SET_CHILDREN_COMPANIES, companies: childrenList })

    if (isUsingRemoteChildren && !isOrphanSelected) {
      return
    }

    const reconciledPending = reconcileSelection(pendingSelection, sortedChildren, selectionLimit)
    const reconciledSelected = reconcileSelection(selectedChildren, sortedChildren, selectionLimit)

    let nextPending = reconciledPending
    let nextSelected = reconciledSelected

    const childIds = sortedChildren.map((child) => child.id)
    const coversAllPendingChildren =
      isAllAccountsSelectable &&
      childIds.length > 0 &&
      childIds.every((id) => nextPending.some((company) => company.id === id))
    const coversAllSelectedChildren =
      isAllAccountsSelectable &&
      childIds.length > 0 &&
      childIds.every((id) => nextSelected.some((company) => company.id === id))

    const shouldAutoSelectAllAccounts =
      isAllAccountsSelectable &&
      sortedChildren.length > 0 &&
      nextPending.length === 0 &&
      nextSelected.length === 0 &&
      !pendingAllAccountsSelection &&
      !isAllAccountsSelected

    if (shouldAutoSelectAllAccounts || coversAllPendingChildren) {
      nextPending = [...sortedChildren]
    }

    if (shouldAutoSelectAllAccounts || coversAllSelectedChildren) {
      nextSelected = [...sortedChildren]
    }

    const nextPendingIsAllAccounts =
      isAllAccountsSelectable &&
      childIds.length > 0 &&
      childIds.every((id) => nextPending.some((company) => company.id === id))
    const nextSelectedIsAllAccounts =
      isAllAccountsSelectable &&
      childIds.length > 0 &&
      childIds.every((id) => nextSelected.some((company) => company.id === id))

    const pendingChanged =
      !areSelectionsEqual(pendingSelection, nextPending) ||
      pendingAllAccountsSelection !== nextPendingIsAllAccounts
    const selectedChanged =
      !areSelectionsEqual(selectedChildren, nextSelected) ||
      isAllAccountsSelected !== nextSelectedIsAllAccounts

    if (pendingChanged) {
      dispatch({
        type: companiesActions.SET_PENDING_SELECTION,
        companies: nextPending,
        isAllAccountsSelected: nextPendingIsAllAccounts,
      })
    }

    if (selectedChanged) {
      dispatch({
        type: companiesActions.APPLY_SELECTION,
        companies: nextSelected,
        isAllAccountsSelected: nextSelectedIsAllAccounts,
      })
    }
  }, [
    childrenQuery.data,
    childrenQuery.isSuccess,
    dispatch,
    isAllAccountsSelected,
    isOrphanSelected,
    isUsingRemoteChildren,
    isLoginB2C,
    pendingAllAccountsSelection,
    pendingSelection,
    selectedChildren,
    selectionLimit,
  ])
}

export { useChildrenList }
